import { create } from 'zustand';
import { doc, setDoc } from '@/lib/firebase/firestore';
import { db, functions } from '@/config/firebase';
import { httpsCallable } from '@/lib/firebase/functions';
import { useAuthStore } from './auth-store';
import { useAppStore } from './app-store';
import { useAppUsersStore } from './app-users-store';
import { useModuleStore } from './module-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OrgJoinRequestStatus = 'pending' | 'accepted' | 'rejected';

export interface OrgJoinRequest {
  requesterUid: string;
  requesterEmail: string;
  requesterName: string;
  requesterPic: string | null;
  adminEmail: string;
  adminName: string | null;
  saasOrgID: string;
  status: OrgJoinRequestStatus;
  createdTime: number;
  respondedTime?: number;
  sourceExt?: string;
}

export interface DomainOrgAdmin {
  uid: string;
  name: string;
  email: string;
  photoURL?: string | null;
  orgName?: string | null;
}

interface OrgJoinStore {
  choiceKey: string;
  choiceLoaded: boolean;
  choiceDone: boolean;
  orgName: string | null;
  sentRequests: Record<string, OrgJoinRequest>;
  domainAdmins: DomainOrgAdmin[];
  domainAdminsLoading: boolean;
  domainAdminsLoaded: boolean;
  domainAdminsReason: string | null;
  receivedRequests: Record<string, OrgJoinRequest>;

  loadMyChoice: (extOverride?: string) => Promise<void>;
  _loadMyChoice: (extOverride?: string) => Promise<void>;
  createOrg: (name: string) => Promise<void>;
  loadDomainAdmins: () => Promise<void>;
  sendJoinRequest: (adminEmail: string, adminName?: string | null) => Promise<void>;
  cancelJoinRequest: (adminEmail: string) => Promise<void>;
  refreshJoinRequests: () => Promise<void>;
  subscribeSentRequests: () => () => void;
  subscribeReceivedRequests: () => () => void;
  respondToRequest: (requesterEmail: string, accept: boolean) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.in', 'ymail.com',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'protonmail.com', 'proton.me', 'zoho.com', 'zohomail.com', 'zohomail.in',
  'gmx.com', 'gmx.net', 'mail.com', 'yandex.com', 'yandex.ru', 'rediffmail.com',
]);

export function getEmailDomain(email: string | null | undefined): string {
  return email?.split('@')[1]?.toLowerCase().trim() || '';
}

export function isCompanyEmail(email: string | null | undefined): boolean {
  const domain = getEmailDomain(email);
  return !!domain && !PUBLIC_EMAIL_DOMAINS.has(domain);
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  return `${local.slice(0, 2)}${'*'.repeat(Math.max(local.length - 2, 2))}@${domain}`;
}

let _choiceInFlight: Promise<void> | null = null;
let _choiceInFlightKey = '';

export function getChoiceKey(uid: string, ext: string): string {
  return `${uid}:${ext}`;
}

function getExt(): string {
  return useAppStore.getState().extensionName || '';
}

const JOIN_REQUESTS_POLL_MS = 20000;
let _joinRequestsInFlight: Promise<void> | null = null;
let _joinRequestsVersion = 0;

function normalizeJoinReq(raw: any, docId: string): OrgJoinRequest {
  const d = raw || {};
  const rawStatus = (d.status || d.requestStatus || 'pending').toString().toLowerCase().trim();
  const status: OrgJoinRequestStatus =
    rawStatus === 'accepted' ? 'accepted' : rawStatus === 'rejected' ? 'rejected' : 'pending';
  const adminEmail = (d.adminEmail || docId).toLowerCase().trim();
  const requesterEmail = (d.requesterEmail || docId).toLowerCase().trim();

  return {
    requesterUid: d.requesterUid || '',
    requesterEmail,
    requesterName: d.requesterName || requesterEmail,
    requesterPic: d.requesterPic || null,
    adminEmail,
    adminName: d.adminName || null,
    saasOrgID: d.saasOrgID || '',
    status,
    createdTime: d.createdTime || Date.now(),
    respondedTime: d.respondedTime,
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useOrgJoinStore = create<OrgJoinStore>((set, get) => ({
  choiceKey: '',
  choiceLoaded: false,
  choiceDone: false,
  orgName: null,
  sentRequests: {},
  domainAdmins: [],
  domainAdminsLoading: false,
  domainAdminsLoaded: false,
  domainAdminsReason: null,
  receivedRequests: {},

  loadMyChoice: (extOverride?: string) => {
    const uid = useAuthStore.getState().user?.uid || '';
    const key = getChoiceKey(uid, extOverride || getExt());
    if (_choiceInFlight && _choiceInFlightKey === key) return _choiceInFlight;
    const run = get()._loadMyChoice(extOverride).finally(() => {
      if (_choiceInFlight === run) _choiceInFlight = null;
    });
    _choiceInFlight = run;
    _choiceInFlightKey = key;
    return run;
  },

  _loadMyChoice: async (extOverride?: string) => {
    const user = useAuthStore.getState().user;
    const ext = extOverride || getExt();
    if (!user || !ext) return;
    const key = getChoiceKey(user.uid, ext);
    if (get().choiceKey !== key) {
      set({ choiceKey: '', choiceLoaded: false, choiceDone: false, orgName: null });
    }

    let cachedDone = false;
    let cachedName: string | null = null;
    try {
      cachedDone = localStorage.getItem(`UA_ORG_CHOICE_DONE_${ext}_${user.uid}`) === 'true';
      cachedName = localStorage.getItem(`UA_ORG_NAME_${ext}_${user.uid}`);
    } catch { /* localStorage unavailable */ }

    let choiceDone = cachedDone;
    let orgName = cachedName;
    let readFailed = false;
    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const res = await callServiceAPI({
        appCode: ext,
        action: 'GET_ORG_SETUP_STATUS',
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
      const data = res.data as { status?: string; hasTpaSetup?: boolean; orgChoiceDone?: boolean; orgName?: string | null };
      if (data?.status !== 'success') throw new Error('GET_ORG_SETUP_STATUS failed');
      if (data.orgChoiceDone || data.orgName || data.hasTpaSetup) {
        choiceDone = true;
      }
      if (data.orgName) {
        orgName = data.orgName;
      }
    } catch (err) {
      console.warn('[org-join-store] loadMyChoice failed:', err);
      readFailed = true;
    }

    if (choiceDone) {
      try {
        localStorage.setItem(`UA_ORG_CHOICE_DONE_${ext}_${user.uid}`, 'true');
        if (orgName) localStorage.setItem(`UA_ORG_NAME_${ext}_${user.uid}`, orgName);
      } catch { /* */ }
    }

    if (useAuthStore.getState().user?.uid !== user.uid || getExt() !== ext) return;
    set({ choiceKey: key, choiceLoaded: true, choiceDone: choiceDone || readFailed, orgName: orgName || null });
  },

  createOrg: async (name: string) => {
    const user = useAuthStore.getState().user;
    const ext = getExt();
    if (!user || !ext) return;
    const orgName = name.trim();
    const now = Date.now();
    const emailDomain = user.email ? getEmailDomain(user.email) : null;

    try {
      localStorage.setItem(`UA_ORG_CHOICE_DONE_${ext}_${user.uid}`, 'true');
      localStorage.setItem(`UA_ORG_NAME_${ext}_${user.uid}`, orgName);
    } catch { /* */ }

    set({ choiceKey: getChoiceKey(user.uid, ext), choiceDone: true, choiceLoaded: true, orgName });

    const writes: Promise<unknown>[] = [
      setDoc(
        doc(db, `ulgebraUsers/${user.uid}/extensions`, ext),
        { orgName, orgChoiceDone: true, orgCreatedTime: now },
        { merge: true },
      ),
    ];
    if (emailDomain) {
      writes.push(setDoc(doc(db, 'ulgebraUsers', user.uid), { emailDomain }, { merge: true }));
    }

    const results = await Promise.allSettled(writes);
    results.forEach((r) => {
      if (r.status === 'rejected') console.warn('[org-join-store] createOrg write failed:', r.reason);
    });
  },

  loadDomainAdmins: async () => {
    const user = useAuthStore.getState().user;
    const ext = getExt();
    if (!user || !ext) return;
    if (!isCompanyEmail(user.email)) {
      set({ domainAdmins: [], domainAdminsLoaded: true, domainAdminsReason: null });
      return;
    }
    set({ domainAdminsLoading: true, domainAdminsReason: null });
    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const res = await callServiceAPI({
        appCode: ext,
        action: 'LIST_DOMAIN_ORG_ADMINS',
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
      const data = res.data as { status?: string; admins?: DomainOrgAdmin[]; reason?: string };
      if (data?.status !== 'success') {
        throw new Error('LIST_DOMAIN_ORG_ADMINS failed');
      }
      const userEmailClean = (user.email || '').toLowerCase().trim();
      const admins = (data?.admins || []).filter(
        (a) => a.email && a.email.toLowerCase().trim() !== userEmailClean,
      );
      set({
        domainAdmins: admins,
        domainAdminsLoading: false,
        domainAdminsLoaded: true,
        domainAdminsReason: data?.reason || null,
      });
    } catch (error) {
      console.error('[org-join-store] loadDomainAdmins error:', error);
      set({ domainAdmins: [], domainAdminsLoading: false, domainAdminsLoaded: true, domainAdminsReason: 'error' });
    }
  },

  sendJoinRequest: async (adminEmailRaw: string, adminName?: string | null) => {
    const user = useAuthStore.getState().user;
    const ext = getExt();
    const adminEmail = adminEmailRaw.trim().toLowerCase();
    const requesterEmail = (user?.email || '').trim().toLowerCase();
    if (!user || !ext || !adminEmail || adminEmail === requesterEmail) return;

    const request: OrgJoinRequest = {
      requesterUid: user.uid,
      requesterEmail,
      requesterName: user.displayName || user.email || '',
      requesterPic: user.photoURL || null,
      adminEmail,
      adminName: adminName || null,
      saasOrgID: useAppStore.getState().appConfig?.orgId || '',
      status: 'pending',
      createdTime: Date.now(),
    };

    const previous = get().sentRequests[adminEmail];
    _joinRequestsVersion++;
    set((s) => ({ sentRequests: { ...s.sentRequests, [adminEmail]: request } }));

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const res = await callServiceAPI({
        appCode: ext,
        action: 'SEND_ORG_JOIN_REQUEST',
        adminEmail,
        adminName: adminName || null,
        saasOrgID: request.saasOrgID,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
      const data = res.data as { status?: string; message?: string };
      if (data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to send join request');
      }
    } catch (err) {
      set((s) => {
        const sentRequests = { ...s.sentRequests };
        if (previous) sentRequests[adminEmail] = previous;
        else delete sentRequests[adminEmail];
        return { sentRequests };
      });
      throw err;
    }
  },

  cancelJoinRequest: async (adminEmailRaw: string) => {
    const ext = getExt();
    const adminEmail = adminEmailRaw.trim().toLowerCase();
    const request = get().sentRequests[adminEmail];
    if (!ext || !request) return;

    const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
    const res = await callServiceAPI({
      appCode: request.sourceExt || ext,
      action: 'CANCEL_ORG_JOIN_REQUEST',
      adminEmail,
      headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
    });
    const data = res.data as { status?: string; message?: string };
    if (data?.status !== 'success') {
      throw new Error(data?.message || 'Failed to cancel join request');
    }

    _joinRequestsVersion++;
    set((s) => {
      const sentRequests = { ...s.sentRequests };
      delete sentRequests[adminEmail];
      return { sentRequests };
    });
  },

  refreshJoinRequests: () => {
    if (_joinRequestsInFlight) return _joinRequestsInFlight;
    const user = useAuthStore.getState().user;
    const ext = getExt();
    if (!user?.email || !ext) return Promise.resolve();

    const startedVersion = _joinRequestsVersion;
    _joinRequestsInFlight = (async () => {
      try {
        const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
        const res = await callServiceAPI({
          appCode: ext,
          action: 'LIST_ORG_JOIN_REQUESTS',
          headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
        });
        const data = res.data as { status?: string; sent?: any[]; received?: any[] };
        if (data?.status !== 'success') return;

        const sentRequests: Record<string, OrgJoinRequest> = {};
        (data.sent || []).filter((d) => d.status !== 'cancelled').forEach((d) => {
          const req = { ...normalizeJoinReq(d, d.docId), sourceExt: d.sourceExt };
          if (!sentRequests[req.adminEmail]) sentRequests[req.adminEmail] = req;
        });
        const receivedRequests: Record<string, OrgJoinRequest> = {};
        (data.received || []).filter((d) => d.status !== 'cancelled').forEach((d) => {
          const req = { ...normalizeJoinReq(d, d.docId), sourceExt: d.sourceExt };
          if (!receivedRequests[req.requesterEmail]) receivedRequests[req.requesterEmail] = req;
        });
        if (startedVersion !== _joinRequestsVersion) return;
        set({ sentRequests, receivedRequests });
      } catch (err) {
        console.warn('[org-join-store] refreshJoinRequests failed:', err);
      } finally {
        _joinRequestsInFlight = null;
      }
    })();
    return _joinRequestsInFlight;
  },

  subscribeSentRequests: () => {
    get().refreshJoinRequests();
    const timer = setInterval(() => get().refreshJoinRequests(), JOIN_REQUESTS_POLL_MS);
    return () => clearInterval(timer);
  },

  subscribeReceivedRequests: () => {
    get().refreshJoinRequests();
    const timer = setInterval(() => get().refreshJoinRequests(), JOIN_REQUESTS_POLL_MS);
    return () => clearInterval(timer);
  },

  respondToRequest: async (requesterEmailRaw: string, accept: boolean) => {
    const user = useAuthStore.getState().user;
    const ext = getExt();
    const requesterEmail = requesterEmailRaw.toLowerCase().trim();
    const request = get().receivedRequests[requesterEmail];
    if (!user || !ext || !request) return;

    if (accept) {
      await useAppUsersStore.getState().addUserByEmail(request.requesterName, requesterEmail);
      if (!useAppUsersStore.getState().orgUsers[requesterEmail]) {
        throw new Error('Could not add the user to the organization');
      }
    }

    const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
    const res = await callServiceAPI({
      appCode: request.sourceExt || ext,
      action: 'RESPOND_ORG_JOIN_REQUEST',
      requesterEmail,
      accept,
      headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
    });
    const data = res.data as { status?: string; message?: string };
    if (data?.status !== 'success') {
      throw new Error(data?.message || 'Failed to respond to request');
    }

    const update = { status: (accept ? 'accepted' : 'rejected') as OrgJoinRequestStatus, respondedTime: Date.now() };
    _joinRequestsVersion++;
    set((s) => ({
      receivedRequests: { ...s.receivedRequests, [requesterEmail]: { ...request, ...update } },
    }));
  },
}));

export function useOrgSetupGateVisible(): boolean {
  const uid = useAuthStore((s) => s.user?.uid);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  const extensionName = useAppStore((s) => s.extensionName);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const choiceKey = useOrgJoinStore((s) => s.choiceKey);
  const choiceDone = useOrgJoinStore((s) => s.choiceDone);

  const needsTpaLogin =
    !!tpaModule && (!!tpaModule.getOAuthInfo?.() || tpaModule.getCredentialFields().length > 0);
  if (!uid || !extensionName || !appCodeTPA || appCodeTPA === 'whatsappweb' || !needsTpaLogin) return false;
  if (selectedAdminUserId || impersonatedUserId) return false;
  return choiceKey === getChoiceKey(uid, extensionName) && !choiceDone;
}
